import { SelectionModel } from '@angular/cdk/collections';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { AccountancyGridItem, AccountancyMessage } from '@skylab-shared/business';
import { DataExchangeStatus } from '@skylab-shared/dataModel';

import { L10n, L10nBase } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { ButtonComponent, DialogContentComponentBase, DialogContentComponentIns, DialogContentViewModel, DialogHeaderComponentIns, DialogInsService, GridGroupItem, GridGroupUtils, GridInsComponent, Menu, MenuItem, MenuType, ServiceGridColumn, ServiceGridOptions } from '@skylab-angular-shared/ui';
import { CopyToClipboardService } from '../../common/copy-to-clipboard/copy-to-clipboard.service';

type AccountancyDataExchangeErrorsGridColumnIdentity = 'message' | 'url' | 'copy';

interface DataExchangeErrorsDialogViewModel extends DialogContentViewModel {
    gridOptions: ServiceGridOptions<AccountancyMessage | GridGroupItem<AccountancyMessage>, AccountancyDataExchangeErrorsGridColumnIdentity, unknown>;
    gridColumns$: BehaviorSubject<ServiceGridColumn<AccountancyDataExchangeErrorsGridColumnIdentity, unknown, AccountancyMessage | GridGroupItem<AccountancyMessage>, unknown, any>[]>;
    gridDataSource$: BehaviorSubject<(AccountancyMessage | GridGroupItem<AccountancyMessage>)[]>;
    isReady: boolean;
}

@Component({
    selector: 'skl-accountancy-data-exchange-errors-dialog.component',
    templateUrl: './accountancy-data-exchange-errors-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, GridInsComponent, ButtonComponent, TranslatePipe]
})
export class AccountancyDataExchangeErrorsDialogComponent extends DialogContentComponentBase<DataExchangeErrorsDialogViewModel, AccountancyGridItem> {
    public constructor(
        @Inject(L10n) private l10n: L10nBase,
        private readonly copyToClipboardService: CopyToClipboardService,
        dialogService: DialogInsService
    ) {
        super(dialogService);
    }

    protected override getInitialState(): DataExchangeErrorsDialogViewModel {
        const grouped = GridGroupUtils.groupBy(
            this.data.messages,
            'severity',
            severity => {
                switch (severity) {
                    case DataExchangeStatus.Error: return this.l10n._T('scenes.accountancy.service.dialog.data-exchange-errors-and-warnings.columns.errors');
                    case DataExchangeStatus.Warning: return this.l10n._T('scenes.accountancy.service.dialog.data-exchange-errors-and-warnings.columns.warnings');
                    default: return '';
                }
            }
        );

        return {
            gridOptions: {
                selection: new SelectionModel(),
                contextMenu: this.gridRowContextMenu
            },
            gridDataSource$: new BehaviorSubject(grouped),
            gridColumns$: new BehaviorSubject(this.gridColumns),
            isReady: false
        };
    }

    private get gridColumns(): ServiceGridColumn<AccountancyDataExchangeErrorsGridColumnIdentity, unknown, AccountancyMessage | GridGroupItem<AccountancyMessage>, unknown, any>[] {
        return [
            {
                identityName: 'message',
                width: '200px',
                fillingColumn: true,
                columnDef: 'text',
                cell: (entity: AccountancyMessage | GridGroupItem<AccountancyMessage>) => 'isGroup' in entity ? '' : (entity as AccountancyMessage).text,
                cellTooltip: (entity: AccountancyMessage | GridGroupItem<AccountancyMessage>) => 'isGroup' in entity ? '' : (entity as AccountancyMessage).text,
                isMovable: false,
                isResizable: false,
            },
            {

                identityName: 'url',
                columnDef: 'url',
                template: 'sklHrefLinkTemplate',
                cell: (entity: AccountancyMessage | GridGroupItem<AccountancyMessage>) => 'isGroup' in entity ? '' : (((entity as AccountancyMessage).redirectState) ? this.l10n._T('scenes.accountancy.service.dialog.data-exchange-errors-and-warnings.repair') : ''),
                getRouteObject: (entity: AccountancyMessage | GridGroupItem<AccountancyMessage>) => 'isGroup' in entity ? { url: '' } : ({url: (entity as AccountancyMessage).redirectState?.url ?? '', params: (entity as AccountancyMessage).redirectState?.params }),
                target: '_self',
                width: '90px',
                minWidth: '90px',
                isMovable: false,
                isResizable: false,
            },
            {
                identityName: 'copy',
                columnDef: 'copy-button',
                template: 'buttonTemplate',
                action: (_, entity) => this.copyToClipboardService.copy((entity as AccountancyMessage).text),
                icon: 'list-copy',
                styleClass: ['cell--button', 'cell--show-on-hover', 'cell--sticky', 'cell--no-space'],
                cellTooltip: _ => this.l10n._T('scenes.accountancy.service.dialog.data-exchange-errors-and-warnings.action.copy'),
                isMovable: false,
                isResizable: false,
            }
        ];
    }

    private get gridRowContextMenu(): Menu<AccountancyMessage> {
        return new Menu<AccountancyMessage>([
            new MenuItem<AccountancyMessage>({
                label: this.l10n._T('scenes.accountancy.service.dialog.data-exchange-errors-and-warnings.action.copy'),
                icon: 'icon-list-copy',
                click: (entity?: AccountancyMessage) => {
                    if (!entity) return;
                    this.copyToClipboardService.copy(entity.text);
                }
            }),
        ], {
            type: MenuType.Context,
            showDisabled: false,
            hideOnScroll: true
        });
    }

    protected override isReady(): void {
        this.viewModel.isReady = true;
    }
}
